import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, BookOpen, X } from "lucide-react";
import { MATERIAIS } from "@/config/site";
import { AccessLink, SoonBadge } from "@/components/AccessLink";
import { IconByName } from "@/components/icons";
import { stopScroll, resumeScroll } from "@/lib/scroll";
import { cn } from "@/lib/utils";

export function openMaterialsModal() {
  window.dispatchEvent(new Event("fw:open-materials"));
}

export default function MaterialsModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("fw:open-materials", onOpen);
    return () => window.removeEventListener("fw:open-materials", onOpen);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    stopScroll();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      resumeScroll();
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Loja FW"
            data-testid="materials-modal"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-[#0d1117]/95 shadow-[0_24px_70px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-brand-teal/40 bg-brand-teal/15 text-brand-blue">
                <IconByName name="ShoppingBag" className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <p className="font-display text-base font-bold text-white">Loja FW</p>
                <p className="text-xs text-slate-400">Escolha o material que quer abrir.</p>
              </div>
              <button
                type="button"
                data-testid="materials-modal-close"
                aria-label="Fechar"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 text-slate-300 transition-colors hover:border-brand-teal/50 hover:text-brand-blue"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col gap-2.5 p-5" data-testid="materials-options">
              {MATERIAIS.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5"
                >
                  <AccessLink
                    href={item.href}
                    external
                    testId={`material-option-${item.id}`}
                    className="flex flex-1 items-center gap-3 text-left"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-teal/40 bg-brand-teal/15 text-brand-blue">
                      <IconByName name={item.icon} className="h-5 w-5" />
                    </span>
                    <span className="flex-1">
                      <span className="block font-display text-sm font-bold leading-snug text-white sm:text-base">
                        {item.label}
                      </span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-slate-400">
                        {item.description}
                      </span>
                    </span>
                  </AccessLink>
                  {item.checkout ? (
                    <button
                      type="button"
                      data-testid={`buy-${item.id}`}
                      onClick={() => document.getElementById(`hotmart-anchor-${item.id}`)?.click()}
                      className="shrink-0 rounded-lg bg-[#59b548] px-4 py-2.5 text-[13px] font-semibold leading-tight text-white transition-colors hover:bg-[#4da33d] active:scale-95"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Comprar Agora
                    </button>
                  ) : (
                    <SoonBadge className="shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}